<template>
  <div class="reservation-detail tongxin-layout">
    <!-- 找不到預約 -->
    <template v-if="!isLoading && !reservation">
      <h1 class="title-with-underline">預約詳情</h1>
      <div class="empty-state">
        <p>找不到此預約紀錄</p>
      </div>
      <button class="btn btn-primary btn-block" @click="goBack">返回</button>
    </template>

    <!-- 取消成功畫面 -->
    <template v-else-if="isCancelSuccess">
      <div class="heading-icon" style="background-color: #dc2626">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <path d="M18 6L6 18M6 6l12 12"></path>
        </svg>
      </div>
      <h1 class="title-with-underline">已取消預約</h1>

      <div class="profile-card">
        <div class="profile-body">
          <div class="info-row">
            <span class="label">看診人</span>
            <span class="value">{{ reservation.summary.patientName || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="label">診所名稱</span>
            <span class="value">{{ reservation.summary.clinicName }}</span>
          </div>
          <div class="info-row">
            <span class="label">看診日期</span>
            <span class="value text-green">{{ formattedDate }}</span>
          </div>
          <div class="info-row">
            <span class="label">預約時段</span>
            <span class="value">{{ reservation.summary.slotText }}</span>
          </div>
          <div class="info-row">
            <span class="label">掛號類型</span>
            <span class="value">{{ reservation.summary.typeName }}</span>
          </div>
        </div>
      </div>

      <button class="btn btn-primary btn-block" @click="goBackToList">返回預約列表</button>
    </template>

    <!-- 預約詳情 -->
    <template v-else-if="reservation">
      <h1 class="title-with-underline">預約詳情</h1>

      <div class="profile-card">
        <!-- 頂部摘要 -->
        <div class="profile-header">
          <div>
            <h2 class="profile-name">{{ reservation.summary.clinicName }}</h2>
            <div class="patient-name" v-if="reservation.summary.patientName">
              {{ reservation.summary.patientName }}
            </div>
          </div>
          <span class="status-badge" :class="statusClass">{{ statusText }}</span>
        </div>

        <!-- 預約資訊 -->
        <div class="profile-body">
          <div
            class="info-row"
            v-for="(item, index) in getProcessedInfoBlock(reservation.summary.infoBlock)"
            :key="index"
          >
            <span class="label">{{ item.title }}</span>
            <span class="value" :class="{ 'text-green': item.title === '就診時段' }">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <button
        v-if="canCancel"
        class="btn btn-danger btn-block"
        @click="showCancelModal = true"
      >
        取消預約
      </button>
      <button class="btn btn-primary btn-block" @click="goBack">返回</button>
    </template>

    <!-- 取消確認 Modal -->
    <transition name="fade">
      <div v-if="showCancelModal" class="modal-overlay" @click.self="showCancelModal = false">
        <div class="modal-card">
          <h2 class="modal-title">確認取消預約</h2>
          <p class="modal-description">取消後將無法恢復，確定要取消此預約嗎？</p>

          <div class="modal-body">
            <div class="info-row" v-for="(item, index) in getProcessedInfoBlock(reservation.summary.infoBlock)" :key="index">
              <span class="label">{{ item.title }}</span>
              <span class="value">{{ item.value }}</span>
            </div>
          </div>

          <div class="modal-actions">
            <button
              class="btn btn-secondary"
              @click="showCancelModal = false"
              :disabled="isCancelling"
            >
              返回
            </button>
            <button
              class="btn btn-danger"
              @click="cancelReservation"
              :disabled="isCancelling"
            >
              {{ isCancelling ? '取消中...' : '確認取消' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <b-spinner style="border-width: 2px" />
    </div>
  </div>
</template>

<script>
import tongxinApi from '@/apis/web/tongxin'

export default {
  name: 'ReservationDetail',
  inject: ['clinicIdData'],
  props: {
    reservationId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      reservation: null,
      isLoading: false,
      showCancelModal: false,
      isCancelling: false,
      isCancelSuccess: false
    }
  },
  computed: {
    clinicId() {
      return this.clinicIdData?.value || null
    },
    formattedDate() {
      if (!this.reservation) return ''
      const date = new Date(this.reservation.summary.visitDate)
      if (isNaN(date.getTime())) return this.reservation.summary.visitDate

      const month = date.getMonth() + 1
      const day = date.getDate()
      const weekday = ['日', '一', '二', '三', '四', '五', '六'][date.getDay()]

      return `${month}/${day} (${weekday})`
    },
    statusClass() {
      if (!this.reservation) return ''
      const statusMap = {
        confirmed: 'status-confirmed',
        pending: 'status-pending',
        cancelled: 'status-cancelled'
      }
      return statusMap[this.reservation.status] || 'status-default'
    },
    statusText() {
      if (!this.reservation) return ''
      const statusMap = {
        confirmed: '已確認',
        pending: '待確認',
        cancelled: '已取消'
      }
      return statusMap[this.reservation.status] || this.reservation.status
    },
    canCancel() {
      if (!this.reservation) return false
      return this.reservation.status !== 'cancelled'
    }
  },
  created() {
    this.fetchReservation()
  },
  methods: {
    /**
     * 處理並格式化 infoBlock 資訊供 Modal 與內文顯示
     */
    getProcessedInfoBlock(infoBlock) {
      if (!Array.isArray(infoBlock)) return []
      
      // 根據 sort 排序
      const sorted = [...infoBlock].sort((a, b) => (a.sort || 0) - (b.sort || 0))
      
      const typeItem = sorted.find(i => i.title === '就診類型')
      const typeName = typeItem ? (typeItem.value !== undefined ? typeItem.value : typeItem.text) : ''
      
      let allowedTitles = ['就診人', '診所名稱', '就診時段', '就診類型', '預計看診號']
      if (typeName === '一般看診') {
        allowedTitles.push('就診科別', '看診醫師')
      } else if (typeName === '體檢/健檢') {
        allowedTitles.push('體檢/健檢種類', '加值項目', '費用')
      } else if (typeName === '疫苗') {
        allowedTitles.push('疫苗種類', '加值項目', '費用')
      } else if (typeName) {
        // 其他未知類型，至少允許基本欄位
      }
      
      const processed = []
      
      for (const item of sorted) {
        let title = item.title
        let value = item.value !== undefined ? item.value : item.text
        
        if (!value) continue
        
        // 限制只顯示允許的欄位
        if (!allowedTitles.includes(title)) continue
        
        if (title === '就診類型') title = '掛號類型'
        if (title === '預計看診號') title = '看診號'
        
        if (title === '就診時段') {
          const date = new Date(value)
          let formatted = value
          if (!isNaN(date.getTime())) {
            const month = date.getMonth() + 1
            const day = date.getDate()
            const weekday = ['日', '一', '二', '三', '四', '五', '六'][date.getDay()]
            formatted = `${month}/${day} (${weekday})`
          }
          const slotItem = sorted.find(i => i.title === '時段')
          if (slotItem && (slotItem.value || slotItem.text)) {
            value = `${formatted} ${slotItem.value || slotItem.text}`
          } else {
            value = formatted
          }
        }
        
        processed.push({ title, value })
      }

      // 需求：將「掛號類型」跟「看診號」顯示的順序對調
      const typeIndex = processed.findIndex(p => p.title === '掛號類型')
      const numIndex = processed.findIndex(p => p.title === '看診號')
      
      if (typeIndex !== -1 && numIndex !== -1) {
        const temp = processed[typeIndex]
        processed[typeIndex] = processed[numIndex]
        processed[numIndex] = temp
      }

      return processed
    },

    /**
     * 取得預約資料
     * 優先從 sessionStorage 快取讀取（列表頁導航時寫入），
     * 若無快取（直接 URL 進入或重新整理）則 fallback 到列表 API 篩選
     */
    async fetchReservation() {
      this.isLoading = true

      try {
        // 優先從快取讀取
        const cached = sessionStorage.getItem('tongxin_current_reservation')
        if (cached) {
          sessionStorage.removeItem('tongxin_current_reservation')
          const data = JSON.parse(cached)
          if (data && data.reservationId === this.reservationId) {
            this.reservation = data
            return
          }
        }

        // Fallback：從列表 API 篩選
        const { data: customerRes = {} } = await tongxinApi.getCustomerInfo()
        const ssid = customerRes.customer_code || ''
        const birthday = customerRes.birthday || ''

        const response = await tongxinApi.getReservations(ssid, birthday, 'all', null, null, 100, 0)
        const list = response.data || response || []
        const reservations = Array.isArray(list) ? list : []

        this.reservation = reservations.find(
          r => r.reservationId === this.reservationId
        ) || null
      } catch (error) {
        console.error('取得預約資料失敗:', error)
        this.reservation = null
      } finally {
        this.isLoading = false
      }
    },

    /**
     * 取消預約
     */
    async cancelReservation() {
      if (this.isCancelling) return

      this.isCancelling = true

      try {
        await tongxinApi.cancelReservation(this.reservationId)

        // 更新本地狀態
        this.reservation.status = 'cancelled'
        this.showCancelModal = false
        this.isCancelSuccess = true
      } catch (error) {
        console.error('取消預約失敗:', error)
        this.$swal({
          type: 'error',
          title: '取消預約失敗，請稍後再試',
          confirmButtonText: '確定'
        })
      } finally {
        this.isCancelling = false
      }
    },

    /**
     * 返回上一頁
     */
    goBack() {
      this.$router.back()
    },

    /**
     * 返回預約列表
     */
    goBackToList() {
      this.$router.push({ name: 'TongxinReservations' })
    }
  }
}
</script>

<style lang="scss" scoped>
.reservation-detail {
  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #999;
    font-size: 16px;
  }

  .profile-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 24px;
    margin-top: 40px;
    margin-bottom: 16px;

    .profile-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid #f0f0f0;

      .profile-name {
        font-size: 24px;
        font-weight: bold;
        color: #333;
        margin: 0 0 4px 0;
      }

      .patient-name {
        font-size: 14px;
        color: #555;
      }

      .status-badge {
        display: inline-block;
        padding: 6px 16px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: bold;
        white-space: nowrap;

        &.status-confirmed {
          background: #e8f5f3;
          color: #207868;
        }

        &.status-pending {
          background: #fef3e8;
          color: #f59e0b;
        }

        &.status-cancelled {
          background: #fce4ec;
          color: #dc2626;
        }

        &.status-default {
          background: #f5f5f5;
          color: #666;
        }
      }
    }

    .profile-body {
      .info-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .label {
          font-size: 16px;
          color: #666;
          font-weight: 500;
        }

        .value {
          font-size: 16px;
          color: #333;
          font-weight: 600;
        }
      }
    }
  }

  // Modal
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
  }

  .modal-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 24px;
    max-width: 420px;
    width: 100%;

    .modal-title {
      font-size: 20px;
      font-weight: bold;
      color: #333;
      margin-bottom: 8px;
      text-align: center;
    }

    .modal-description {
      font-size: 14px;
      color: #666;
      margin-bottom: 20px;
      text-align: center;
    }

    .modal-body {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 4px 16px;
      margin-bottom: 24px;

      .info-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .label {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .value {
          font-size: 14px;
          color: #333;
          font-weight: 600;
        }
      }
    }

    .modal-actions {
      display: flex;
      gap: 12px;

      .btn {
        flex: 1;
      }
    }
  }
}

// 動畫
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
